import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useRouter from 'utils/useRouter'
import { usePericiandoService } from 'hooks/usePericiandoService'
import { useLocal } from 'hooks/useLocal'
import axios from 'axios'
import moment from 'moment'

export const useFormPericiando = (props) => {
  const { route, idCaso, idPericiando } = props

  const router = useRouter()
  const localService = useLocal()
  const periciandoService = usePericiandoService()

  // Estados dos valores do periciando buscados na API
  const [periciando, setPericiando] = useState({})
  const [processandoPericiando, setProcessandoPericiando] = useState(false)
  const [processandoOperacao, setProcessandoOperacao] = useState(false)

  // Estados dos arquivos relacionados
  const [arquivos, setArquivos] = useState([])
  const arquivosIniciais = useMemo(() => periciando?.arqs?.map((arquivo) => arquivo.id) || [], [periciando])

  // Estados auxiliares de qualificação e localização
  const [paises, setPaises] = useState([])
  const [estados, setEstados] = useState([])
  const [cidades, setCidades] = useState([])
  const [cidadesNaturalidade, setCidadesNaturalidade] = useState([])

  const [tipo, setTipo] = useState(null)
  const [tipoPrisao, setTipoPrisao] = useState([])
  const [categoriaPrisao, setCategoriaPrisao] = useState([])
  const [naturezaSolicitacao, setNaturezaSolicitacao] = useState([])

  const [arrayFiliacoesPericiando, setArrayFiliacoesPericiando] = useState(['filiacao0', 'filiacao1'])
  const [formValido, setFormValido] = useState(false)
  const [openEspelhoConfirmacao, setOpenEspelhoConfirmacao] = useState(false)
  const [addressData, setAddressData] = useState({})
  const [mensagensAlerta, setMensagensAlerta] = useState([])
  const [openAlert, setOpenAlert] = useState(false)

  const endereco = useSelector((state) => state.mapa)

  // Inicialização do estado do formulário nativo do React
  const [formState, setFormStateState] = useState({
    values: {
      cpf: '',
      rg: '',
      nomeSocial: '',
      estadoCivil: null,
      nacionalidade: null,
      naturalidade: null,
      naturalidadeCidade: null,
      dataNascimento: null,
      sexo: '',
      nome: '',
      alcunha: '',
      nomeSocial: '',
      cep: '',
      endereco: '',
      numeroResidencial: '',
      municipio: null,
      bairro: '',
      complemento: '',
      ocupacao: '',
      escolaridade: '',
    },
    errors: {},
    touched: {},
  })

  const setFormState = useCallback((updateFnOrObject) => {
    setFormStateState((prev) => {
      const result = typeof updateFnOrObject === 'function' ? updateFnOrObject(prev) : updateFnOrObject
      return {
        ...prev,
        ...result,
        values: {
          ...prev.values,
          ...(result.values || {}),
        },
        errors: {
          ...prev.errors,
          ...(result.errors || {}),
        },
      }
    })
  }, [])

  const handleChange = useCallback((event) => {
    const { name, value } = event.target
    setFormStateState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [name]: value,
      },
    }))
  }, [])

  const handleChangeNewValueByNameProperty = useCallback((event, newValue, name) => {
    setFormStateState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [name]: newValue,
      },
    }))
  }, [])

  const handleChangeCampoData = useCallback((name, value) => {
    setFormStateState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [name]: value,
      },
    }))
  }, [])

  const handleChangeWithModification = useCallback((event, modifier) => {
    const { name, value } = event.target
    setFormStateState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [name]: modifier(value),
      },
    }))
  }, [])

  const hasError = useCallback((name) => {
    return Boolean(formState.errors[name])
  }, [formState.errors])

  const hasErrorWithoutTouch = useCallback((name) => {
    return Boolean(formState.errors[name])
  }, [formState.errors])

  const formPericiando = {
    formState,
    setFormState,
    handleChange,
    handleChangeNewValueByNameProperty,
    handleChangeCampoData,
    handleChangeWithModification,
    hasError,
    hasErrorWithoutTouch,
  }

  // Validação CPF
  const validateCPF = useCallback((number) => {
    if (!number) return false
    const cpf = number.replace(/[/.-]/g, '')

    let Soma = 0
    let Resto

    for (let i = 1; i <= 9; i += 1) {
      Soma += parseInt(cpf.substring(i - 1, i), 10) * (11 - i)
    }

    Resto = (Soma * 10) % 11

    if (Resto === 10 || Resto === 11) {
      Resto = 0
    }

    if (Resto !== parseInt(cpf.substring(9, 10), 10)) {
      return false
    }

    Soma = 0

    for (let j = 1; j <= 10; j += 1) {
      Soma += parseInt(cpf.substring(j - 1, j), 10) * (12 - j)
    }

    Resto = (Soma * 10) % 11

    if (Resto === 10 || Resto === 11) {
      Resto = 0
    }

    if (Resto !== parseInt(cpf.substring(10, 11), 10)) {
      return false
    }

    return true
  }, [])

  const [cpfValido, setCpfValido] = useState(false)

  useEffect(() => {
    setCpfValido(validateCPF(formPericiando.formState.values.cpf))
  }, [formPericiando.formState.values.cpf, validateCPF])

  // Busca de dados iniciais do IBGE
  const fetchPaises = useCallback(async () => {
    try {
      const paisesData = await localService.listaDePaises()
      setPaises(paisesData || [])
    } catch (err) {
      console.error(err)
    }
  }, [])

  const fetchEstados = useCallback(async () => {
    try {
      const response = await localService.listaDeEstadosDoBrasil()
      setEstados(response || [])
    } catch (err) {
      setEstados([])
    }
  }, [])

  const fetchCidadesRN = useCallback(async () => {
    try {
      const response = await localService.listarCidadesPorEstadoAsync('rn')
      setCidades(response || [])
    } catch (err) {
      setCidades([])
    }
  }, [])

  useEffect(() => {
    fetchPaises()
    fetchEstados()
    fetchCidadesRN()
  }, [fetchPaises, fetchEstados, fetchCidadesRN])

  const fetchCidades = useCallback(async (siglaEstado) => {
    const response = await localService.listarCidadesPorEstadoAsync(siglaEstado)
    return response || []
  }, [])

  // Busca do Periciando para Edição
  const fetchPericiando = useCallback(async () => {
    if (route.editar && idPericiando) {
      const response = await periciandoService.listarPericiandoDetalhadoAsync(idPericiando, setProcessandoPericiando)
      setPericiando(response || {})
    }
  }, [route.editar, idPericiando])

  useEffect(() => {
    fetchPericiando()
  }, [fetchPericiando])

  // Inicializar o formulário quando o periciando for carregado
  useEffect(() => {
    if (periciando && periciando.id) {
      let objectFiliacoes = {}
      if (periciando.filiacoes) {
        let arrayFiliacoes = []
        periciando.filiacoes.forEach((filiacao, index) => {
          objectFiliacoes = {
            ...objectFiliacoes,
            [`filiacao${index}`]: filiacao,
          }
          arrayFiliacoes = arrayFiliacoes.concat(`filiacao${index}`)
        })
        if (!arrayFiliacoes.length) {
          arrayFiliacoes = ['filiacao0', 'filiacao1']
        }
        setArrayFiliacoesPericiando(arrayFiliacoes)
      }

      // Normalizar campos que podem vir como string do backend
      let nacionalidadeObj = periciando.nacionalidade
      if (typeof periciando.nacionalidade === 'string') {
        nacionalidadeObj = { nome: periciando.nacionalidade }
      }

      let naturalidadeObj = periciando.naturalidade
      if (typeof periciando.naturalidade === 'string') {
        naturalidadeObj = { sigla: periciando.naturalidade }
      }

      let naturalidadeCidadeObj = periciando.naturalidadeCidade
      if (typeof periciando.naturalidadeCidade === 'string') {
        naturalidadeCidadeObj = { nome: periciando.naturalidadeCidade }
      }

      let municipioObj = periciando.municipio
      if (typeof periciando.municipio === 'string') {
        municipioObj = { nome: periciando.municipio }
      }

      formPericiando.setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          ...periciando,
          nacionalidade: nacionalidadeObj,
          naturalidade: naturalidadeObj,
          naturalidadeCidade: naturalidadeCidadeObj,
          municipio: municipioObj,
          ...objectFiliacoes,
        },
      }))
      setTipo(periciando.tipo)
      setCategoriaPrisao(periciando.categoriasPrisao?.map((cp) => cp.categoria) || [])
      setTipoPrisao(periciando.tiposPrisao?.map((tp) => tp.tipo) || [])
      setNaturezaSolicitacao(periciando.naturezasSolicitacao?.map((ns) => ns.natureza) || [])

      // Se já tiver naturalidade, carregar as cidades correspondentes
      if (periciando.naturalidade) {
        const sigla = typeof periciando.naturalidade === 'string' ? periciando.naturalidade : periciando.naturalidade.sigla
        if (sigla) {
          fetchCidades(sigla).then((cidadesRes) => {
            setCidadesNaturalidade(cidadesRes)
          })
        }
      }
    }
  }, [periciando, fetchCidades])

  // Sincronizar objetos completos quando as listas forem carregadas
  useEffect(() => {
    if (periciando && periciando.id) {
      formPericiando.setFormState((prevState) => {
        const currentNac = prevState.values.nacionalidade
        const currentNat = prevState.values.naturalidade

        let updatedNac = currentNac
        if (currentNac && paises.length > 0) {
          const matched = paises.find(
            (p) => p.nome === currentNac.nome || p.nome === currentNac
          )
          if (matched && matched !== currentNac) updatedNac = matched
        }

        let updatedNat = currentNat
        if (currentNat && estados.length > 0) {
          const matched = estados.find(
            (e) => e.sigla === currentNat.sigla || e.nome === currentNat.nome || e.sigla === currentNat
          )
          if (matched && matched !== currentNat) updatedNat = matched
        }

        if (updatedNac !== currentNac || updatedNat !== currentNat) {
          return {
            ...prevState,
            values: {
              ...prevState.values,
              nacionalidade: updatedNac,
              naturalidade: updatedNat,
            },
          }
        }
        return prevState
      })
    }
  }, [paises, estados, periciando])

  // Sincronizar município com a lista de cidades do estado
  useEffect(() => {
    if (periciando && periciando.id && cidades.length > 0) {
      formPericiando.setFormState((prevState) => {
        const currentMun = prevState.values.municipio
        if (currentMun) {
          const matched = cidades.find((c) => {
            const currentName = typeof currentMun === 'string' ? currentMun : currentMun?.nome
            return c.nome?.toUpperCase() === currentName?.toUpperCase()
          })
          if (matched && matched !== currentMun) {
            return {
              ...prevState,
              values: {
                ...prevState.values,
                municipio: matched,
              },
            }
          }
        }
        return prevState
      })
    }
  }, [cidades, periciando])

  // Sincronizar naturalidadeCidade com a lista de cidades da naturalidade
  useEffect(() => {
    if (periciando && periciando.id && cidadesNaturalidade.length > 0) {
      formPericiando.setFormState((prevState) => {
        const currentNatCid = prevState.values.naturalidadeCidade
        if (currentNatCid) {
          const matched = cidadesNaturalidade.find((c) => {
            const currentName = typeof currentNatCid === 'string' ? currentNatCid : currentNatCid?.nome
            return c.nome?.toUpperCase() === currentName?.toUpperCase()
          })
          if (matched && matched !== currentNatCid) {
            return {
              ...prevState,
              values: {
                ...prevState.values,
                naturalidadeCidade: matched,
              },
            }
          }
        }
        return prevState
      })
    }
  }, [cidadesNaturalidade, periciando])

  // Adição de Filiação Dinâmica
  const handleAdicionarFiliacao = useCallback(() => {
    const newInput = `filiacao${arrayFiliacoesPericiando.length}`
    setArrayFiliacoesPericiando((prev) => prev.concat(newInput))
  }, [arrayFiliacoesPericiando])

  // Validação Geral de Formulário reativa
  useEffect(() => {
    const tempErrors = {}
    const hasNome = formState.values.nome && formState.values.nome.trim()
    const hasNomeSocial = formState.values.nomeSocial && formState.values.nomeSocial.trim()
    const hasAlcunha = formState.values.alcunha && formState.values.alcunha.trim()

    if (!hasNome && !hasNomeSocial && !hasAlcunha) {
      tempErrors.nome = ['Nome, nome social ou alcunha é obrigatório']
      tempErrors.nomeSocial = ['Nome, nome social ou alcunha é obrigatório']
      tempErrors.alcunha = ['Nome, nome social ou alcunha é obrigatório']
    }

    setFormStateState((prev) => {
      const hasChanged = JSON.stringify(prev.errors) !== JSON.stringify(tempErrors)
      if (hasChanged) {
        return {
          ...prev,
          errors: tempErrors,
        }
      }
      return prev
    })

    setFormValido(Object.keys(tempErrors).length === 0)
  }, [formState.values.nome, formState.values.alcunha, formState.values.nomeSocial])

  // Busca CEP
  const handleSearchCep = useCallback(() => {
    if (formPericiando.formState.values.cep) {
      axios
        .get(`https://viacep.com.br/ws/${formPericiando.formState.values.cep}/json/`)
        .then((response) => {
          setAddressData(response.data)
        })
        .catch((error) => {
          console.error('Error fetching CEP data:', error)
          setAddressData({})
        })
    }
  }, [formPericiando.formState.values.cep])

  useEffect(() => {
    if (addressData && addressData.cep) {
      formPericiando.setFormState((prevFormState) => ({
        ...prevFormState,
        values: {
          ...prevFormState.values,
          cep: addressData.cep,
          endereco: addressData.logradouro || '',
          municipio: addressData.cep ? { nome: addressData.localidade } : null,
          estadoEndereco: addressData.cep ? { sigla: addressData.uf } : null,
          bairro: addressData.bairro || '',
          complemento: addressData.complemento || '',
        },
      }))
    }
  }, [addressData])

  // Monitorar alterações de endereço via Redux (mapa)
  useEffect(() => {
    if (endereco.dados && endereco.dados[0]) {
      const dadosEndereco = endereco.dados[0].address
      const municipio = dadosEndereco.city || dadosEndereco.city_district

      formPericiando.setFormState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          endereco: dadosEndereco.road || '',
          numeroResidencial: prev.values.numeroResidencial || '',
          municipio: municipio ? { nome: municipio } : null,
          estadoEndereco: { sigla: dadosEndereco.estadoEndereco } || null,
          bairro: dadosEndereco.suburb || '',
        },
      }))
    }
  }, [endereco])

  // Handlers
  const handleChangeTipoPericiando = useCallback((valor) => {
    setTipo(valor)
    setTipoPrisao([])
    setCategoriaPrisao([])
    setNaturezaSolicitacao([])
  }, [])

  const handleChangeNaturalidadeEstado = useCallback(async (event, newValue) => {
    formPericiando.handleChangeNewValueByNameProperty(event, newValue, 'naturalidade')
    formPericiando.handleChangeNewValueByNameProperty(event, null, 'naturalidadeCidade')

    if (newValue) {
      const cidadesRes = await fetchCidades(newValue.sigla)
      setCidadesNaturalidade(cidadesRes)
    } else {
      setCidadesNaturalidade([])
    }
  }, [formPericiando, fetchCidades])

  // Submissão do Formulário
  const handleBotaoCadastrarEditar = async () => {
    const cpfAtual = formPericiando.formState.values.cpf

    if (cpfAtual) {
      if (!cpfValido) {
        setMensagensAlerta(['O CPF informado é inválido'])
        setOpenAlert(true)
        return
      }
      const cpfJaExiste = await periciandoService.consultarCPFExistente(cpfAtual)
      if (!route.editar && cpfJaExiste && cpfJaExiste.length > 0) {
        setMensagensAlerta(['O CPF informado já foi cadastrado no sistema'])
        setOpenAlert(true)
        return
      }
    }

    setOpenEspelhoConfirmacao(true)
  }

  const handleCloseAlert = () => {
    setMensagensAlerta([])
    setOpenAlert(false)
  }

  const handleConfirmarOperacao = useCallback(async () => {
    const filiacoes = []
    Object.keys(formPericiando.formState.values).forEach((key) => {
      if (key.indexOf('filiacao') !== -1 && formPericiando.formState.values[key]) {
        filiacoes.push(formPericiando.formState.values[key])
      }
    })

    const dados = {
      ...formPericiando.formState.values,
      id_caso: periciando.id ? periciando.id_caso : idCaso,
      naturalidade: formPericiando.formState.values.naturalidade?.sigla || formPericiando.formState.values.naturalidade,
      naturalidadeCidade: formPericiando.formState.values.naturalidadeCidade?.nome || formPericiando.formState.values.naturalidadeCidade,
      nacionalidade: formPericiando.formState.values.nacionalidade?.nome || formPericiando.formState.values.nacionalidade,
      tipo: tipo,
      tipoPrisao: tipoPrisao,
      categoriaPrisao: categoriaPrisao,
      naturezaSolicitacao: naturezaSolicitacao,
      arquivos,
      filiacoes,
    }

    if (route.editar) {
      await periciandoService.editarPericiando(periciando.id, dados, setProcessandoOperacao)
      router.history.push(`/casos/caso/periciando/detalhar/${periciando.id}`)
    } else {
      const novoPericiando = await periciandoService.cadastrarPericiando(dados, setProcessandoOperacao)
      router.history.push(`/casos/caso/detalhar/${idCaso}`)
    }
  }, [formPericiando, route, idCaso, periciando, arquivos, tipo, tipoPrisao, categoriaPrisao, naturezaSolicitacao, router])

  // Vinculo automático de arquivos
  useEffect(() => {
    formPericiando.setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        arquivos: arquivos || [],
      },
    }))
  }, [arquivos])

  // Mascaras
  const cpfMask = useCallback((value) => {
    if (!value) return ''
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1')
  }, [])

  const cepMask = useCallback((value) => {
    if (!value) return ''
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{3})\d+?$/, '$1')
  }, [])

  return {
    periciando,
    processandoPericiando,
    processandoOperacao,
    arquivos,
    setArquivos,
    arquivosIniciais,
    paises,
    estados,
    cidades,
    cidadesNaturalidade,
    tipo,
    tipoPrisao,
    setTipoPrisao,
    categoriaPrisao,
    setCategoriaPrisao,
    naturezaSolicitacao,
    setNaturezaSolicitacao,
    arrayFiliacoesPericiando,
    formValido,
    openEspelhoConfirmacao,
    setOpenEspelhoConfirmacao,
    mensagensAlerta,
    openAlert,
    formPericiando,
    cpfValido,
    handleAdicionarFiliacao,
    handleSearchCep,
    handleChangeTipoPericiando,
    handleChangeNaturalidadeEstado,
    handleBotaoCadastrarEditar,
    handleCloseAlert,
    handleConfirmarOperacao,
    cpfMask,
    cepMask,
  }
}
